import dayjs, { Dayjs } from 'dayjs';
import { useState } from 'react';
import { DateCalendar, MobileDatePicker } from '@mui/x-date-pickers';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Stack,
  Box,
  Typography,
} from '@mui/material';

import { Strings } from '@app/constants';
import { Header } from '@app/sections/header';
import { formatTimeSlot, isSameSlot } from './utils';
import useResponsive from '@app/hooks/use-responsive';

// ---------------------------------------------------------------

interface Props {
  open: boolean;
  onClose: VoidFunction;

  /*
   value: [slot-start,slot-end] ([DD-MM-YYYY HH:mm,DD-MM-YYYY HH:mm])
  */
  onSelect: (value: string[]) => void;

  // In hrs
  slotDuration: number;

  // Selected slots
  initialValue?: string[];

  // Slot starting hours
  startsHours: number;

  // Slot ending hours
  endsTo: number;

  // to check if want to select multiple slots or single
  isMultipleSelectable?: boolean;
  shouldDisableDate: any;
}

export function TimeSlotPicker({
  open,
  onClose,
  onSelect,
  slotDuration,
  initialValue = [],
  startsHours,
  isMultipleSelectable,
  endsTo,
  shouldDisableDate,
}: Props) {
  const [selectedSlot, setSelectedSlots] = useState<string[]>(initialValue);
  const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs());

  /*
    Slot should start from startsHours, and it should
    not be past so exclude any past slots 
  */
  const isFutureDateSelected = dayjs().isBefore(selectedDate, 'date');
  const scheduleFrom = isFutureDateSelected
    ? startsHours
    : Math.max(dayjs().hour(), startsHours) + 1;

  const slots = [];
  let currentSlotTime = dayjs(selectedDate).set('hour', scheduleFrom).set('minute', 0);

  while (currentSlotTime.isBefore(dayjs(selectedDate).set('hour', endsTo), 'minute')) {
    const slotStart = currentSlotTime.toISOString();
    const slotEnd = currentSlotTime.add(slotDuration, 'minute').toISOString();
    slots.push(`${slotStart},${slotEnd}`);
    currentSlotTime = currentSlotTime.add(slotDuration, 'minute');
  }

  const handleSlotClick = (slot: string) => {
    if (isMultipleSelectable) {
      setSelectedSlots((prevSelectedSlots: string[]) => {
        const isSelected = prevSelectedSlots.some((selected) => isSameSlot(selected, slot));
        if (isSelected) {
          return prevSelectedSlots.filter((prevSlot) => !isSameSlot(prevSlot, slot));
        } else {
          return [...prevSelectedSlots, slot];
        }
      });
    } else {
      setSelectedSlots([slot]);
    }
  };

  const handleSaveClick = () => {
    onSelect(selectedSlot);
    onClose();
  };

  const handleDateSelection = (date: Dayjs | null) => {
    if (date) {
      setSelectedDate(date);
    }
  };

  const isSlotActive = (slot: string) =>
    selectedSlot.some((activeSlot: string) => isSameSlot(slot, activeSlot));

  const isMobileView = useResponsive('down', 'sm');

  return (
    <Dialog
      onClose={onClose}
      open={open}
      fullWidth
      sx={{
        '& .MuiDialog-container': {
          '& .MuiPaper-root': {
            width: '100%',
            maxWidth: '700px',
          },
        },
      }}
    >
      <Header title={Strings.addCandidate.selectCandidate} onClose={onClose} variant="dialog" />

      <DialogContent
        dividers
        sx={{
          display: 'flex',
          alignItems: 'center',
          p: 2,
          py: 3,

          flexDirection: !isMobileView ? 'row' : 'column',
        }}
      >
        <Box>
          {isMobileView ? (
            <Stack>
              <MobileDatePicker
                sx={{ width: '80vw' }}
                value={selectedDate}
                onChange={handleDateSelection}
                shouldDisableDate={shouldDisableDate}
                disablePast={true}
              />
            </Stack>
          ) : (
            <DateCalendar
              value={selectedDate}
              shouldDisableDate={shouldDisableDate}
              onChange={handleDateSelection}
              disablePast={true}
              sx={{
                border: (theme) => `0.5px solid ${theme.palette.primary.main}`,
                borderRadius: 1,
              }}
            />
          )}
        </Box>

        {slots.length === 0 ? (
          <Typography variant="h6" textAlign={'center'}>
            {Strings.caption.thereAreNoSlots}
          </Typography>
        ) : (
          <Stack
            direction="row"
            flexWrap="wrap"
            justifyContent={'center'}
            sx={{ width: isMobileView ? '80vw' : 'auto' }}
          >
            {slots.map((slot: string) => (
              <Button
                color={isSlotActive(slot) ? 'primary' : 'inherit'}
                variant={isSlotActive(slot) ? 'contained' : 'outlined'}
                onClick={() => {
                  handleSlotClick(slot);
                }}
                sx={{
                  borderRadius: 1,
                  p: 1,
                  m: 0.4,
                  boxShadow: (theme) => theme.customShadows.z4,
                  textTransform: 'initial',
                }}
                key={slot}
              >
                <Typography variant="body2">{formatTimeSlot(slot)}</Typography>
              </Button>
            ))}
          </Stack>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="inherit" variant="outlined">
          {Strings.button.cancel}
        </Button>
        <Button variant="contained" onClick={handleSaveClick}>
          {Strings.button.save}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
